import React from "react";
import { $Button, $Tooltip, $TextArea, $Popconfirm } from "us.common/components";
import { DocumentType } from "constants/TemplateEditor";
import { RootState } from "us.helper/types";
import { SectionPicker } from "../SectionPicker";
import * as Actions from "actions";
import { connect, ConnectedProps } from "react-redux";
import { useTranslation } from "react-i18next";
import { CloseOutlined, PlusOutlined } from "us.icons";
import { scrollIntoPage } from "./Functions";
import { isArray } from "lodash";
import { Formik } from "formik";
import { CommonProperties } from "../CommonProperties";
import { getUrlParams } from "components/EditorHome/Functions";
import { useLocation } from "react-router-dom";

type PagesAndSectionsProps = {
  isXml?: boolean;
  activePageIndex: number;
};

const PagesAndSections: React.FC<PropsFromRedux & PagesAndSectionsProps> = (
  props
) => {
  const {
    isXml,
    template,
    activePageIndex,
    addNewPage,
    deletePage,
    setProperties,
  } = props;
  const { t } = useTranslation();
  const { search } = useLocation();
  const { documentType } = getUrlParams(search);

  const { templateContent, description } = template.data ?? {};
  const pageCount = templateContent?.pages?.length ?? 0;

  return (
    <Formik
      initialValues={{ description }}
      enableReinitialize
      validateOnChange
      validateOnBlur
      onSubmit={() => { }}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        resetForm,
        ...restProps
      }: any) => (
        <>
          {!isXml && (
            <>
              <div className="panel-title pr-1">
                <h4 className="d-flex align-items-center pl-2 py-2 m-0">
                  {`${t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.PAGES")} ${pageCount && pageCount !== 1 ? `(${pageCount})` : ""
                    }`}
                </h4>

                <div className="d-flex align-items-center">
                  <$Tooltip
                    placement="right"
                    title={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.NEW_PAGE")}
                  >
                    <$Button
                      icon={<PlusOutlined />}
                      type="link"
                      size="small"
                      onClick={() => addNewPage && addNewPage({})}
                      disabled={
                        !template?.data || documentType == DocumentType.EMAIL
                      }
                    />
                  </$Tooltip>
                </div>
              </div>
              <div className="pages-content p-2 mb-2">
                {isArray(templateContent?.pages) &&
                  templateContent.pages?.map((page: any, index: number) => (
                    <div
                      key={index.toString()}
                      className={`d-flex justify-content-between py-1 px-2 mb-1 editor-list-item ${activePageIndex == index ? " active" : ""
                        }`}
                      onClick={() => scrollIntoPage(index)}
                    >
                      <div className="d-flex align-items-center text-muted">
                        {`${t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.PAGE")} ${index + 1
                          }`}
                      </div>
                      {pageCount !== 1 && (
                        <div className="item-more-button">
                          <$Popconfirm
                            title={t(
                              "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.REMOVE_PAGE_CONFIRM"
                            )}
                            onConfirm={() =>
                              deletePage && deletePage({ pageIndex: index })
                            }
                            okText={t("US.COLLECTION.COMMON:COMMON.YES")}
                            cancelText={t("US.COLLECTION.COMMON:COMMON.NO")}
                          >
                            <$Tooltip
                              placement="right"
                              title={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.REMOVE_PAGE")}
                            >
                              <$Button
                                type="link"
                                size="small"
                                icon={<CloseOutlined className="text-error" />}
                              />
                            </$Tooltip>
                          </$Popconfirm>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </>
          )}
          {!isXml && <SectionPicker />}
          <div className="panel-title pr-1">
            <h4 className="d-flex align-items-center pl-2 py-2 m-0">
              {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.TEMPLATE_DESCRIPTION")}
            </h4>
          </div>

          <div className="components-content p-2 mb-2">
            <$TextArea
              name="description"
              style={{ width: "100%" }}
              required
              onChange={(e: any) => {
                e.target.value &&
                  setProperties &&
                  setProperties({ description: e.target.value });
                restProps.setFieldValue("activeState", e.target.value);
              }}
              disabled={!template?.data}
            />
          </div>

          <div className="panel-title pr-1">
            <h4 className="d-flex align-items-center pl-2 py-2 m-0">
              {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.PROPERTIES")}
            </h4>
          </div>
          <CommonProperties isXml={isXml} />
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templatesHistory, templateEditor } = state;
  const { template } = templatesHistory;
  const { section, parameters, adapters, drawerInfo } = templateEditor;

  return {
    template,
    section,
    parameters,
    adapters,
    drawerInfo,
  };
};

const { pages, template } = Actions.templatesHistory;

const mapDispatchToProps = {
  setProperties: pages.setProperties,
  addNewPage: pages.addNewPage,
  deletePage: pages.deletePage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(PagesAndSections);
